import { Component } from '@angular/core';
import { PricingService } from 'src/app/features/pricing/pricing.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-account-margin',
  template: `
    <div class="margin-wrapper">
      <h3>Margem da Conta</h3>
      <p class="hint">
        Defina uma margem que será somada aos valores dos produtos da conta se a margem for ativada
        na hora de criar o produto. Em reais, o valor é somado direto; em porcentagem, é aplicado
        sobre o valor base do produto.
      </p>

      <div class="row">
        <mat-form-field appearance="outline" class="tipo-field">
          <mat-label>Tipo</mat-label>
          <mat-select [(ngModel)]="type" (ngModelChange)="onTypeChange()">
            <mat-option value="valor">Reais (R$)</mat-option>
            <mat-option value="percentual">Porcentagem (%)</mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>{{ type === 'percentual' ? 'Margem (%)' : 'Margem (R$)' }}</mat-label>
          <input matInput type="text" inputmode="decimal" pattern="[0-9.,]*" [ngModel]="display"
                 (keydown)="onKeyDown($event)" (ngModelChange)="onChange($event)" placeholder="0,00">
        </mat-form-field>

        <button mat-raised-button color="primary" (click)="save()" [disabled]="loading">
          Salvar
        </button>
      </div>

      <mat-progress-bar *ngIf="loading" mode="indeterminate"></mat-progress-bar>
    </div>
  `,
  styles: [`
    .margin-wrapper { padding: 16px; }
    .row { display:flex; gap: 12px; align-items: center; max-width: 620px; flex-wrap: wrap; }
    .hint { color:#666; margin-bottom: 12px; max-width: 620px; }
    mat-form-field { flex: 1; }
    .tipo-field { max-width: 200px; }
  `]
})
export class AccountMarginComponent {
  value = 0;
  display = '';
  type: 'valor' | 'percentual' = 'valor';
  loading = false;

  /** Estado salvo, usado para saber se a alteração muda os preços já cadastrados. */
  private savedValue = 0;
  private savedType: 'valor' | 'percentual' = 'valor';

  constructor(
    private pricing: PricingService,
    private snack: MatSnackBar,
    private dialog: MatDialog
  ){
    this.fetch();
  }

  fetch(){
    this.loading = true;
    this.pricing.getAccountMargin().subscribe({
      next: (res:any)=>{
        const v = res?.data?.margin_add_value ?? 0;
        this.value = Number(v) || 0;
        this.type = res?.data?.margin_type === 'percentual' ? 'percentual' : 'valor';
        this.savedValue = this.value;
        this.savedType = this.type;
        this.display = this.formatNumber(this.value);
      },
      error: () => {},
      complete: ()=> this.loading = false
    })
  }

  save(){
    if (this.value === this.savedValue && this.type === this.savedType){
      this.snack.open('Nenhuma alteração na margem', 'OK', { duration: 2500 });
      return;
    }

    // A margem recalcula todos os produtos que a utilizam, então o usuário confirma antes
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Alterar a margem da conta?',
        message: 'Todos os produtos que usam a margem serão recalculados agora, e o preço final '
          + 'deles muda de uma vez. Isso afeta o que já está vinculado e em uso com esses produtos, '
          + 'como veículos e adesões. Deseja continuar?',
        confirmText: 'Alterar margem',
        cancelText: 'Cancelar',
        type: 'warning'
      },
      panelClass: 'custom-dialog-container'
    }).afterClosed().subscribe((confirmado: boolean) => {
      if (confirmado) { this.persist(); }
    });
  }

  private persist(){
    this.loading = true;
    this.pricing.updateAccountMargin(this.value, this.type).subscribe({
      next: ()=> {
        this.savedValue = this.value;
        this.savedType = this.type;
        this.snack.open('Margem atualizada', 'OK', { duration: 2500 });
      },
      error: ()=> this.snack.open('Erro ao atualizar margem', 'Fechar', { duration: 3000 }),
      complete: ()=> this.loading = false
    })
  }

  onChange(text: string){
    const onlyDigits = (text || '').toString().replace(/\D+/g, '');
    if (!onlyDigits){ this.display=''; this.value = 0; return; }
    // Porcentagem e inteira; em reais os digitos entram como centavos
    const n = this.type === 'percentual'
      ? parseInt(onlyDigits.slice(0, 3), 10)
      : parseInt(onlyDigits, 10) / 100;
    this.value = Number.isFinite(n) ? n : 0;
    this.display = this.formatNumber(this.value);
  }

  onTypeChange(){
    // Porcentagem nao aceita decimais: trunca o que veio do outro formato
    if (this.type === 'percentual'){ this.value = Math.trunc(this.value); }
    this.display = this.value ? this.formatNumber(this.value) : '';
  }

  onKeyDown(ev: KeyboardEvent){
    const allowed = ['Backspace','Tab','ArrowLeft','ArrowRight','Delete'];
    if (allowed.includes(ev.key)) return;
    // Separador decimal so faz sentido em reais
    if ((ev.key === ',' || ev.key === '.') && this.type !== 'percentual') return;
    if (!/\d/.test(ev.key)) { ev.preventDefault(); }
  }

  private formatNumber(n: number){
    try {
      const casas = this.type === 'percentual' ? 0 : 2;
      return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas })
        .format(n);
    } catch { return ''; }
  }
}
